import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { m } from 'motion/react';
import { Inbox } from 'lucide-react';

import { ApprovalCard } from './ApprovalCard';
import { approvals, type Approval } from '../../api/tasks';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBanner } from '../../components/ErrorBanner';
import { SkeletonRows } from '../../components/Skeleton';
import { PageHeader } from '../../components/Surface';
import { RISE, STAGGER } from '../../components/motion';
import { useT } from '../../i18n/provider';
import { useCompanyId } from '../company/useCompany';

/**
 * Las aprobaciones de *esta* empresa (`GET /companies/{id}/approvals`).
 *
 * La bandeja de `13 §3.8` es personal y cruza empresas, y por eso vive fuera,
 * en el espacio de trabajo. Pero las compuertas paran tareas de una empresa
 * concreta, y quien está dentro mirando su tablero no debería tener que salir
 * del ámbito para contestarlas: esta pantalla es la misma bandeja acotada a la
 * empresa por la que se navega. Mismas tarjetas, mismas reglas —el lote solo
 * para lo `bulk_approvable`— y el mismo sondeo, porque una compuerta nueva
 * llega sola, sin que nadie recargue.
 */
export function CompanyApprovalsScreen() {
  const t = useT();
  const queryClient = useQueryClient();
  const companyId = useCompanyId();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const pending = useQuery({
    queryKey: ['approvals', 'company', companyId],
    queryFn: () => approvals.company(companyId, { status: 'pending' }),
    enabled: companyId !== '',
    refetchInterval: 15_000,
    staleTime: 0,
  });

  const refresh = () => {
    setSelected(new Set());
    void queryClient.invalidateQueries({ queryKey: ['approvals'] });
  };

  const bulk = useMutation({
    mutationFn: () => approvals.bulkApprove([...selected]),
    onSuccess: refresh,
  });

  if (pending.isPending) return <SkeletonRows rows={3} />;
  if (pending.error !== null) return <ErrorBanner error={pending.error} onRetry={() => void pending.refetch()} />;

  const rows = pending.data?.data ?? [];

  const toggle = (approval: Approval, checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current);

      if (checked) next.add(approval.id);
      else next.delete(approval.id);

      return next;
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('approvals.title', { count: rows.length })}
        description={t('approvals.companyIntro')}
        actions={
          selected.size > 0 ? (
            <Button busy={bulk.isPending} onClick={() => bulk.mutate()}>
              {t('approvals.approveSelected', { count: selected.size })}
            </Button>
          ) : null
        }
      />

      {bulk.error !== null ? <ErrorBanner error={bulk.error} /> : null}

      {rows.length === 0 ? (
        <EmptyState
          icon={<Inbox />}
          title={t('approvals.nothingTitle')}
          description={t('approvals.companyNothingHint')}
        />
      ) : (
        <m.ul variants={STAGGER} initial="hidden" animate="shown" className="space-y-4">
          {rows.map((approval) => (
            <m.li key={approval.id} variants={RISE} layout>
              <ApprovalCard
                approval={approval}
                selected={selected.has(approval.id)}
                onSelect={(checked) => toggle(approval, checked)}
                onDecided={refresh}
              />
            </m.li>
          ))}
        </m.ul>
      )}
    </div>
  );
}
